import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, CircularProgress,
  Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
  Tooltip, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


interface Address {
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

interface NfseCompany {
  id: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  inscricao_municipal: string;
  inscricao_estadual: string;
  regime_tributario: string;
  cnae: string;
  phone: string;
  email: string;
  nfse_api_token: string;
  nfse_environment: string;
  is_active: boolean;
  is_default: boolean;
  notes: string;
  address: Address;
  created_at: string;
  updated_at: string;
}

const regimeLabels: Record<string, string> = {
  simples_nacional: 'Simples Nacional',
  lucro_presumido: 'Lucro Presumido',
  lucro_real: 'Lucro Real',
};

const envLabels: Record<string, string> = {
  production: 'Produção',
  homologacao: 'Homologação',
};

const emptyAddress: Address = {
  logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', cep: '',
};

const emptyForm = {
  razao_social: '', nome_fantasia: '', cnpj: '', inscricao_municipal: '', inscricao_estadual: '',
  regime_tributario: 'simples_nacional', cnae: '', phone: '', email: '', nfse_api_token: '',
  nfse_environment: 'homologacao', is_active: true, is_default: false, notes: '',
  address: { ...emptyAddress },
};

const NfseCompanies: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [companies, setCompanies] = useState<NfseCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<NfseCompany | null>(null);
  const [form, setForm] = useState<typeof emptyForm>({ ...emptyForm, address: { ...emptyAddress } });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/financial-module/nfse-companies'));
      setCompanies(res.data?.data || res.data || []);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao carregar empresas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, address: { ...emptyAddress } });
    setDialogOpen(true);
  };

  const openEdit = (company: NfseCompany) => {
    setEditing(company);
    setForm({
      razao_social: company.razao_social || '',
      nome_fantasia: company.nome_fantasia || '',
      cnpj: company.cnpj || '',
      inscricao_municipal: company.inscricao_municipal || '',
      inscricao_estadual: company.inscricao_estadual || '',
      regime_tributario: company.regime_tributario || 'simples_nacional',
      cnae: company.cnae || '',
      phone: company.phone || '',
      email: company.email || '',
      nfse_api_token: company.nfse_api_token || '',
      nfse_environment: company.nfse_environment || 'homologacao',
      is_active: company.is_active ?? true,
      is_default: company.is_default ?? false,
      notes: company.notes || '',
      address: company.address ? { ...emptyAddress, ...company.address } : { ...emptyAddress },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.razao_social || !form.cnpj) {
      setSnackbar({ open: true, message: 'Razão Social e CNPJ são obrigatórios', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(getApiUrl(`/financial-module/nfse-companies/${editing.id}`), form);
        setSnackbar({ open: true, message: 'Empresa atualizada com sucesso', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/nfse-companies'), form);
        setSnackbar({ open: true, message: 'Empresa criada com sucesso', severity: 'success' });
      }
      setDialogOpen(false);
      loadCompanies();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao salvar empresa', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/nfse-companies/${id}`));
      setSnackbar({ open: true, message: 'Empresa excluída com sucesso', severity: 'success' });
      loadCompanies();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao excluir empresa', severity: 'error' });
    }
  };

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field: keyof Address, value: string) => {
    setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
  };

  if (loading && companies.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Empresas - Nota Fiscal</Typography>
            <Typography variant="body2" color="text.secondary">Cadastro de empresas para emissão de NFSe</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadCompanies} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Nova Empresa
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }} elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Razão Social</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Nome Fantasia</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>CNPJ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Inscrição Municipal</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Regime</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Ambiente</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Padrão</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                    Nenhuma empresa cadastrada. Clique em "Nova Empresa" para começar.
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{company.razao_social}</Typography>
                    </TableCell>
                    <TableCell>{company.nome_fantasia || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{company.cnpj}</Typography>
                    </TableCell>
                    <TableCell>{company.inscricao_municipal || '-'}</TableCell>
                    <TableCell>{regimeLabels[company.regime_tributario] || company.regime_tributario || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={envLabels[company.nfse_environment] || company.nfse_environment}
                        size="small"
                        sx={{
                          bgcolor: company.nfse_environment === 'production' ? '#dcfce7' : '#fef3c7',
                          color: company.nfse_environment === 'production' ? '#16a34a' : '#d97706',
                          fontWeight: 600, fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {company.is_default ? (
                        <StarIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                      ) : (
                        <StarBorderIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={company.is_active ? 'Ativo' : 'Inativo'}
                        size="small"
                        sx={{
                          bgcolor: company.is_active ? '#dcfce7' : '#fee2e2',
                          color: company.is_active ? '#16a34a' : '#dc2626',
                          fontWeight: 600, fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(company)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(company.id)} sx={{ color: '#ef4444' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editing ? 'Editar Empresa' : 'Nova Empresa'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Razão Social *" value={form.razao_social}
                onChange={(e) => updateField('razao_social', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Nome Fantasia" value={form.nome_fantasia}
                onChange={(e) => updateField('nome_fantasia', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="CNPJ *" value={form.cnpj}
                onChange={(e) => updateField('cnpj', e.target.value)} placeholder="00.000.000/0000-00" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Inscrição Municipal" value={form.inscricao_municipal}
                onChange={(e) => updateField('inscricao_municipal', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Inscrição Estadual" value={form.inscricao_estadual}
                onChange={(e) => updateField('inscricao_estadual', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Regime Tributário</InputLabel>
                <Select value={form.regime_tributario} label="Regime Tributário"
                  onChange={(e) => updateField('regime_tributario', e.target.value)}>
                  <MenuItem value="simples_nacional">Simples Nacional</MenuItem>
                  <MenuItem value="lucro_presumido">Lucro Presumido</MenuItem>
                  <MenuItem value="lucro_real">Lucro Real</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="CNAE" value={form.cnae}
                onChange={(e) => updateField('cnae', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Telefone" value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Email" value={form.email}
                onChange={(e) => updateField('email', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Token API NFSe" value={form.nfse_api_token}
                onChange={(e) => updateField('nfse_api_token', e.target.value)} type="password" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Ambiente NFSe</InputLabel>
                <Select value={form.nfse_environment} label="Ambiente NFSe"
                  onChange={(e) => updateField('nfse_environment', e.target.value)}>
                  <MenuItem value="production">Produção</MenuItem>
                  <MenuItem value="homologacao">Homologação</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={form.is_active} onChange={(e) => updateField('is_active', e.target.checked)} />}
                label="Ativa"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={form.is_default} onChange={(e) => updateField('is_default', e.target.checked)} />}
                label="Empresa Padrão"
              />
            </Grid>

            {/* Address section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mt: 1 }}>Endereço</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Logradouro" value={form.address.logradouro}
                onChange={(e) => updateAddress('logradouro', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" label="Número" value={form.address.numero}
                onChange={(e) => updateAddress('numero', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Complemento" value={form.address.complemento}
                onChange={(e) => updateAddress('complemento', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Bairro" value={form.address.bairro}
                onChange={(e) => updateAddress('bairro', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Cidade" value={form.address.cidade}
                onChange={(e) => updateAddress('cidade', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" label="UF" value={form.address.uf}
                onChange={(e) => updateAddress('uf', e.target.value)} inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" label="CEP" value={form.address.cep}
                onChange={(e) => updateAddress('cep', e.target.value)} placeholder="00000-000" />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {saving ? 'Salvando...' : editing ? 'Atualizar' : 'Criar Empresa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NfseCompanies;
